import React from 'react';
import BaseFactory from './BaseFactory';
import {
  getFaqsAnchorTag,
  getBlogAnchorTag,
  hasLocationHours,
  getNearbySolvPartner,
  getNearbyBrandLocation,
  getBrandName,
  getRetailPartner,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  getWalkInClinicAnchorTag,
  hasCustomDescription,
} from './util';
import { isEmptyArray } from '../../../core/util/array';
import { isEmptyObject } from '../../../core/util/object';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class NonPartnerRetailClinic extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);
    this.ratingCountStr =
      this.ratingCount > 0 ? ` across more than ${this.ratingCount} total reviews` : '';

    this.retailPartner = getRetailPartner(location);

    if (!isEmptyObject(nearbyLocations) && !isEmptyObject(nearbyLocations.nearby)) {
      this.nearbyLocations = nearbyLocations;
      this.nearbyClinics = nearbyLocations.nearby;
      const nearbySolvPartner = getNearbySolvPartner(this.nearbyClinics);

      // general nearby locations (solv partner or not)
      this.clinicsToChooseFrom = [...this.nearbyClinics];
      if (!isEmptyObject(nearbySolvPartner)) {
        this.clinicsToChooseFrom.shift();
      }

      // nearby brand location
      const nearbyBrandClinic = getNearbyBrandLocation(nearbyLocations);
      this.brandName = getBrandName(nearbyBrandClinic);
    }
  }

  getDescription() {
    return (
      `${this.clinicName} is a retail clinic located ` +
      `${this.retailPartner ? `inside of ${this.retailPartner} ` : ''}` +
      `at ${this.address}, ${this.city}, ` +
      `${this.state}, ${this.zip}. Similar to an urgent care, they treat non-life-threatening ` +
      'symptoms and conditions and wee walk-in patients with no appointments. For more ' +
      `information, call ${this.clinicName}${this.phoneString}.`
    );
  }

  getKeywords() {
    return `${`${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${
      this.zip
    }, ` +
      `retail clinic ${this.city}, retail clinic ${this.zip}, ` +
      `walk-in clinic ${this.city}, walk-in clinic ${this.zip}, `}${
      this.retailPartner
        ? `${this.retailPartner} clinic ${this.city}, ${this.retailPartner} clinic ${this.zip}, `
        : ''
    }${this.city} ${this.state} walk-in clinic, ${this.zip} walk-in clinic`;
  }

  getBody() {
    return (
      <div>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          {this.clinicName} is a <b>retail clinic</b>
          {this.retailPartner && (
            <span>
              {' '}
              inside of <b>{this.retailPartner}</b>
            </span>
          )}{' '}
          in {this.cityAnchorTag}
          {hasLocationHours(this.location)
            ? ` and is open ${this.nextOpenDayName} from ${this.nextOpenDayHoursStr}. `
            : " but unfortunately we don't currently know the hours in which the clinic is open. "}
          They are located at {this.fullAddress}
          {hasLocationHours(this.location) && (
            <span> and open {this.numDaysOpenString} per week</span>
          )}
          . Of course it's always best to call in advance to confirm hours of operation before
          walking in.
        </NoTopMarginParagraph>
        <p>
          Retail clinics offer a wide range of services overlapping considerably with those of a
          traditional {getWalkInClinicAnchorTag()} center. That said, they are usually staffed by a
          Practitioner (NP) rather than a medical doctor and generally do not have an on-site x-ray
          or lab services, which will prevent them from seeing patients with more advanced injuries
          and illnesses.
        </p>
        {isEmptyArray(this.glowingWords) && this.rating && (
          <p>
            <b>{this.clinicName}</b> has received an average rating of {this.rating} stars out of 5
            {this.ratingCountStr}.
          </p>
        )}
        {!isEmptyArray(this.glowingWords) && !this.rating && (
          <p>
            Patients have used words like {this.glowingWordsString} to describe their experience at{' '}
            <b>{this.clinicName}</b>
            {this.ratingCountStr}.
          </p>
        )}
        {!isEmptyArray(this.glowingWords) && this.rating && (
          <p>
            With patients using words like {this.glowingWordsString} commonly in their reviews,{' '}
            <b>{this.clinicName}</b> has received an average rating of {this.rating} stars out of 5
            {this.ratingCountStr}.
          </p>
        )}
        <NoBottomMarginParagraph>
          If you are looking to learn more about retail clinics including statistics, trends,{' '}
          {getFaqsAnchorTag()} and other useful information, check out our {getBlogAnchorTag()} to
          empower your knowledge as a healthcare consumer. {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </div>
    );
  }
}

export default NonPartnerRetailClinic;
